import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';

const Highlights = () => {
  const { isArabic } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);

  const items = [
    {
      id: 1,
      image: "/ramadan1.jpg",
      enTitle: "Ramadan Kareem",
      arTitle: "رمضان كريم",
      enCaption: "Wishing you a month of blessings and joy.",
      arCaption: "نتمنى لكم شهراً مليئاً بالخير والبركات.",
    },
    {
      id: 2,
      image: "/ramadan2.jpg",
      enTitle: "Ramadan Kareem",
      arTitle: "رمضان كريم",
      enCaption: " 'Indeed there is for the fasting person, when he breaks his fast, a supplication which is not rejected' -Ibn Majah " ,
      arCaption: "إن للصائم إذا أفطر دعاءً لا يرد» - ابن ماجه.",
    },
    // {
    //   id: 2,
    //   image: "https://images.unsplash.com/photo-1555529669-e69e7aa0ba9a?q=80&w=1200",
    //   enTitle: "Weekly Bakery Offer",
    //   arTitle: "عرض المخبوزات الأسبوعي",
    //   enCaption: "Buy 2 Get 1 Free on all fresh croissants.",
    //   arCaption: "اشترِ ٢ واحصل على ١ مجاناً على جميع الكرواسان.",
    // },
    {
      id: 3,
      image: "saudi2.jpg",
      enTitle: "Saudi National Day",
      arTitle: "اليوم الوطني السعودي",
      enCaption: "Celebrating 95 years of glory and pride.",
      arCaption: "نحتفل بـ ٩٥ عاماً من المجد والفخر.",
    },
    // {
    //   id: 4,
    //   image: "https://images.unsplash.com/photo-1542838132-92c53300491e?q=80&w=1200",
    //   enTitle: "Platinum Specials",
    //   arTitle: "عروض بلاتينيوم",
    //   enCaption: "Fresh arrivals at unbeatable prices.",
    //   arCaption: "منتجات طازجة بأسعار لا تقبل المنافسة.",
    // },
    {
      id: 5,
      image: "https://images.unsplash.com/photo-1504674900247-0877df9cc836?q=80&w=1200",
      enTitle: "New Menu Launch",
      arTitle: "إطلاق القائمة الجديدة",
      enCaption: "Taste the new flavors at Afam Restaurant.",
      arCaption: "تذوق النكهات الجديدة في مطعم أقام.",
    }
  ];

  // Next Slide Logic
  const nextSlide = () => {
    setCurrentIndex((prev) => (prev === items.length - 1 ? 0 : prev + 1));
  };

  // Previous Slide Logic
  const prevSlide = () => {
    setCurrentIndex((prev) => (prev === 0 ? items.length - 1 : prev - 1));
  };

  // Auto-swap logic (10 seconds) - FIXED
  useEffect(() => {
    const timer = setInterval(() => {
      // Using functional update directly here fixes the dependency issue
      setCurrentIndex((prev) => (prev === items.length - 1 ? 0 : prev + 1));
    }, 10000);
// Only depend on the length of items
    return () => clearInterval(timer);
  }, [items.length]); 

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className={`flex items-center gap-4 mb-10 ${isArabic ? 'flex-row-reverse' : ''}`}>
          <div className="p-3 bg-saudi-green rounded-2xl text-white">
            <Sparkles size={28} />
          </div>
          <div className={isArabic ? 'text-right' : 'text-left'}>
            <h2 className="text-3xl font-bold text-corp-blue">
              {isArabic ? 'العروض والتهاني' : 'Offers & Wishes'}
            </h2>
          </div>
        </div>

        {/* Carousel Container */}
        <div className="relative h-[500px] md:h-[600px] rounded-[3rem] overflow-hidden shadow-2xl group">
          
          {/* Images */}
          {items.map((item, index) => (
            <div
              key={item.id}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                index === currentIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
              }`}
            >
              <img 
                src={item.image} 
                className="w-full h-full object-cover scale-105"
                alt="Highlight" 
              />
              <div className="absolute inset-0 bg-gradient-to-t from-corp-blue/90 via-transparent to-transparent"></div>
              
              {/* Content Overlay */}
              <div className={`absolute bottom-16 left-0 right-0 p-8 md:p-16 ${isArabic ? 'text-right' : 'text-left'}`}>
                <div className="animate-in slide-in-from-bottom-5 duration-700">
                   <h3 className="text-4xl md:text-6xl font-bold text-white mb-4">
                    {isArabic ? item.arTitle : item.enTitle}
                  </h3>
                  <p className="text-xl text-gray-200 max-w-2xl">
                    {isArabic ? item.arCaption : item.enCaption}
                  </p>
                </div>
              </div>
            </div>
          ))}

          {/* Navigation Buttons */}
          <div className="absolute inset-0 z-20 flex items-center justify-between p-4 opacity-0 group-hover:opacity-100 transition-opacity">
            <button 
              onClick={prevSlide}
              className="p-4 rounded-full bg-white/10 backdrop-blur-md text-white hover:bg-saudi-green transition-all"
            >
              <ChevronLeft size={32} />
            </button>
            <button 
              onClick={nextSlide}
              className="p-4 rounded-full bg-white/10 backdrop-blur-md text-white hover:bg-saudi-green transition-all"
            >
              <ChevronRight size={32} />
            </button>
          </div>

          {/* Progress Indicators (Dots) */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 flex gap-3">
            {items.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`h-2 rounded-full transition-all duration-500 ${
                  index === currentIndex ? 'w-12 bg-saudi-green' : 'w-2 bg-white/50'
                }`}
              />
            ))}
          </div>

          {/* 10-Second Visual Progress Bar */}
          <div className="absolute bottom-0 left-0 h-1.5 bg-saudi-green/30 w-full z-30">
            <div 
              key={currentIndex}
              className="h-full bg-saudi-green animate-progress"
              style={{ animationDuration: '10s' }}
            ></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Highlights;